/**
 * ProductVocabularyManager — background scanner for look-alike product UNITS
 * and CATEGORIES ("BAG"/"BAGS", "Pack"/"PACK"/"PACKS", "Fertilizer"/
 * "Fertilizers"). Owner 2026-09-19: "follow the same pattern as the customer
 * [duplicate tool] — ask me which one to use, then merge … popup, ignore for a
 * few hours."  Same UX as CustomerDedupeManager:
 *   - Silent scan on mount + every 5 min + on window focus.
 *   - A floating pill appears when look-alikes exist; it opens the dialog.
 *   - Per group: pick the spelling to KEEP (most-used is preselected), then
 *     Merge the others into it — or mark a spelling "Different".
 *   - Snooze (localStorage) hides the pill and pauses scanning.
 * Admin / owner only — a merge is a catalog-wide edit (the API enforces it).
 * A merge renames the CATALOG; documents already printed keep their spelling.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Tags, RefreshCw, Merge, Clock, ChevronDown, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const SCAN_INTERVAL_MS = 5 * 60 * 1000;
const SKIP_KEY = 'agribooks_vocab_scan_skip';
const SKIP_OPTIONS = [
  { label: '30 minutes', ms: 30 * 60 * 1000 },
  { label: '1 hour', ms: 60 * 60 * 1000 },
  { label: '4 hours', ms: 4 * 60 * 60 * 1000 },
  { label: 'Until tomorrow', ms: 12 * 60 * 60 * 1000 },
];
const FIELD_LABEL = { unit: 'Units', category: 'Categories' };
const FIELD_NOUN = { unit: 'unit', category: 'category' };

const readSkip = () => {
  try {
    const until = parseInt(localStorage.getItem(SKIP_KEY) || '0', 10);
    if (until > Date.now()) return until;
    localStorage.removeItem(SKIP_KEY);
  } catch { /* private mode — no snooze, the tool still works */ }
  return null;
};

export default function ProductVocabularyManager() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.is_owner === true;
  const skipForSuperAdmin = !!user && (user.is_super_admin || !user.organization_id);
  const enabled = !!user && isAdmin && !skipForSuperAdmin;

  const [scan, setScan] = useState({ unit: { clusters: [], oddities: [] }, category: { clusters: [], oddities: [] } });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [skipUntil, setSkipUntil] = useState(readSkip);
  const [showSkipMenu, setShowSkipMenu] = useState(false);
  const [keepers, setKeepers] = useState({});   // clusterId → spelling to keep
  const [busy, setBusy] = useState('');         // clusterId being saved
  const skipMenuRef = useRef(null);

  const runScan = useCallback(async (silent = true) => {
    if (!enabled) return;
    setScanning(true);
    try {
      const { data } = await api.get('/products/vocabulary/duplicates');
      setScan({ unit: data.unit, category: data.category });
      if (!silent) {
        toast.success(data.total_clusters === 0
          ? 'Units and categories are clean.'
          : `Found ${data.total_clusters} group${data.total_clusters === 1 ? '' : 's'} of look-alike spellings.`);
      }
    } catch {
      if (!silent) toast.error('Could not scan units and categories');
    }
    setScanning(false);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    runScan(true);
    // A snooze that ran out brings the pill back by itself.
    const tick = () => { if (!readSkip()) { setSkipUntil(null); runScan(true); } };
    const timer = setInterval(tick, SCAN_INTERVAL_MS);
    window.addEventListener('focus', tick);
    // ImportPage / ProductsPage can ask for a rescan after they add products…
    const onRescan = () => runScan(true);
    // …and the Products page has a quiet door for when the pill is snoozed.
    const onOpen = () => { runScan(true); setDialogOpen(true); };
    window.addEventListener('product-vocabulary-rescan', onRescan);
    window.addEventListener('product-vocabulary-open', onOpen);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', tick);
      window.removeEventListener('product-vocabulary-rescan', onRescan);
      window.removeEventListener('product-vocabulary-open', onOpen);
    };
  }, [enabled, runScan]);

  useEffect(() => {
    const h = (e) => { if (skipMenuRef.current && !skipMenuRef.current.contains(e.target)) setShowSkipMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const applySkip = (ms) => {
    const until = Date.now() + ms;
    try { localStorage.setItem(SKIP_KEY, String(until)); } catch { /* ignore */ }
    setSkipUntil(until);
    setShowSkipMenu(false);
    setDialogOpen(false);
    toast.info(`Snoozed the units and categories review for ${Math.round(ms / 60000)} minutes`);
  };
  const clearSkip = () => {
    try { localStorage.removeItem(SKIP_KEY); } catch { /* ignore */ }
    setSkipUntil(null);
    setShowSkipMenu(false);
    runScan(true);
  };

  const keeperOf = (c) => keepers[c.id] || c.keeper;

  const merge = async (field, cluster) => {
    const keep = keeperOf(cluster);
    const others = cluster.members.filter(m => m.value !== keep);
    setBusy(cluster.id);
    try {
      const { data } = await api.post('/products/vocabulary/merge',
        { field, keep, merge: others.map(m => m.value) });
      toast.success(`${data.products_updated} ${data.products_updated === 1 ? 'product now uses' : 'products now use'} “${keep}”`);
      window.dispatchEvent(new CustomEvent('product-vocabulary-changed'));
      await runScan(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Merge failed');
    }
    setBusy('');
  };

  const markDifferent = async (field, cluster, member) => {
    setBusy(cluster.id);
    try {
      // "Different" is judged against the spelling being kept
      await api.post('/products/vocabulary/mark-distinct', { field, values: [keeperOf(cluster), member.value] });
      toast.success(`“${member.value}” won’t be flagged against “${keeperOf(cluster)}” again`);
      await runScan(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save that');
    }
    setBusy('');
  };

  if (!enabled) return null;

  const groupCount = scan.unit.clusters.length + scan.category.clusters.length;
  const productCount = [...scan.unit.clusters, ...scan.category.clusters].reduce((s, c) => s + c.total, 0);
  const showPill = groupCount > 0 && !skipUntil;

  return (
    <>
      {showPill && (
        // Stacked ABOVE the customer-duplicates pill (bottom-5 left-72) so the
        // two never overlap; bottom-left keeps both off the cart's checkout rail.
        <div className="fixed bottom-[4.5rem] left-72 z-[60]" data-testid="vocab-pill-wrapper">
          <Button onClick={() => setDialogOpen(true)} data-testid="open-vocab-dialog-btn"
            className="bg-amber-500 hover:bg-amber-600 text-white shadow-lg animate-pulse-slow rounded-full h-11 px-4">
            <Tags size={15} className="mr-2" />
            {groupCount} look-alike unit{groupCount === 1 ? '' : 's'} / categor{groupCount === 1 ? 'y' : 'ies'}
            <Badge className="ml-2 bg-white/20 text-white border-0 text-[10px]">{productCount} products</Badge>
          </Button>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="vocab-dialog">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2 justify-between" style={{ fontFamily: 'Manrope' }}>
              <span className="flex items-center gap-2">
                <Tags size={18} className="text-amber-600" />
                Look-alike Units and Categories
                {groupCount > 0 && (
                  <Badge className="bg-amber-100 text-amber-700 border-amber-300 ml-2 text-[10px]">
                    {groupCount} group{groupCount === 1 ? '' : 's'}
                  </Badge>
                )}
              </span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => runScan(false)} disabled={scanning} data-testid="vocab-rescan-btn">
                  <RefreshCw size={12} className={`mr-1 ${scanning ? 'animate-spin' : ''}`} /> Rescan
                </Button>
                <div className="relative" ref={skipMenuRef}>
                  <Button size="sm" variant="outline" onClick={() => setShowSkipMenu(v => !v)} data-testid="vocab-snooze-btn">
                    <Clock size={12} className="mr-1" /> Snooze <ChevronDown size={10} className="ml-1" />
                  </Button>
                  {showSkipMenu && (
                    <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded shadow-lg z-50 min-w-[140px]">
                      {SKIP_OPTIONS.map(opt => (
                        <button key={opt.label} onClick={() => applySkip(opt.ms)}
                          className="block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-100">{opt.label}</button>
                      ))}
                      {skipUntil && (
                        <>
                          <div className="border-t border-slate-100" />
                          <button onClick={clearSkip}
                            className="block w-full text-left px-3 py-1.5 text-xs text-emerald-600 hover:bg-slate-100">Clear snooze</button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              The same word spelled more than one way splits your reports, prints and transfer totals.
              Pick the spelling to keep, then merge. This renames your products — receipts and transfers
              already printed keep the spelling they were printed with.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto mt-2 pr-1 space-y-6" data-testid="vocab-cluster-list">
            {groupCount === 0 && (
              <div className="text-center py-14 text-slate-400">
                <Tags size={36} className="mx-auto mb-3 opacity-40" />
                <p className="text-sm font-medium">Units and categories are clean.</p>
                <p className="text-xs mt-1">We’ll keep scanning in the background.</p>
              </div>
            )}

            {['unit', 'category'].map(field => scan[field].clusters.length > 0 && (
              <section key={field} data-testid={`vocab-section-${field}`}>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">{FIELD_LABEL[field]}</h3>
                <div className="space-y-3">
                  {scan[field].clusters.map(cluster => {
                    const keep = keeperOf(cluster);
                    const others = cluster.members.filter(m => m.value !== keep);
                    const moving = others.reduce((s, m) => s + m.count, 0);
                    const saving = busy === cluster.id;
                    return (
                      <div key={cluster.id} className="border border-amber-200 bg-amber-50/30 rounded-lg p-3"
                           data-testid={`vocab-cluster-${cluster.id}`}>
                        <p className="text-xs text-slate-500 mb-2">
                          {cluster.members.length} spellings of one {FIELD_NOUN[field]} · {cluster.total} products.
                          Which one do you want to use?
                        </p>
                        <div className="space-y-1.5">
                          {cluster.members.map(m => {
                            const isKeep = m.value === keep;
                            return (
                              <div key={m.value}
                                   className={`flex items-center gap-3 rounded-md border px-3 py-2 ${isKeep ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white'}`}
                                   data-testid={`vocab-member-${cluster.id}-${m.value}`}>
                                <label className="flex items-center gap-2 min-w-0 flex-1 cursor-pointer">
                                  <input type="radio" name={`keep-${cluster.id}`} checked={isKeep} disabled={saving}
                                    onChange={() => setKeepers(k => ({ ...k, [cluster.id]: m.value }))}
                                    className="accent-emerald-600" data-testid={`vocab-keep-${cluster.id}-${m.value}`} />
                                  <span className="font-mono text-sm font-semibold text-slate-800">{m.value}</span>
                                  <span className="text-xs text-slate-500 shrink-0">{m.count} product{m.count === 1 ? '' : 's'}</span>
                                  <span className="text-[11px] text-slate-400 truncate hidden sm:inline"
                                        title={(m.samples || []).map(s => s.name).join(' · ')}>
                                    {(m.samples || []).map(s => s.name).join(' · ')}
                                  </span>
                                </label>
                                {isKeep
                                  ? <Badge className="bg-emerald-600 text-white border-0 text-[10px] shrink-0">Keep</Badge>
                                  : (
                                    <Button size="sm" variant="ghost" disabled={saving}
                                      onClick={() => markDifferent(field, cluster, m)}
                                      className="h-7 px-2 text-xs text-slate-500 shrink-0"
                                      title={`“${m.value}” is a genuinely different ${FIELD_NOUN[field]} from “${keep}”`}
                                      data-testid={`vocab-different-${cluster.id}-${m.value}`}>
                                      Different
                                    </Button>
                                  )}
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex items-center justify-between gap-3 mt-2.5">
                          <p className="text-xs text-slate-500">
                            {moving} product{moving === 1 ? '' : 's'} will change from {others.map(m => `“${m.value}”`).join(', ')} to <b className="text-slate-700">“{keep}”</b>.
                          </p>
                          <Button size="sm" disabled={saving || others.length === 0} onClick={() => merge(field, cluster)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0" data-testid={`vocab-merge-${cluster.id}`}>
                            {saving ? <RefreshCw size={12} className="mr-1 animate-spin" /> : <Merge size={12} className="mr-1" />}
                            Merge into “{keep}”
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}

            {(scan.unit.oddities.length > 0) && (
              <section data-testid="vocab-oddities">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1 flex items-center gap-1.5">
                  <AlertTriangle size={12} className="text-amber-500" /> Units used by only one product
                </h3>
                <p className="text-xs text-slate-500 mb-2">Often a typo. Nothing here is changed for you — open the product and fix its unit if it is wrong.</p>
                <div className="flex flex-wrap gap-1.5">
                  {scan.unit.oddities.map(o => (
                    <span key={o.value} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs"
                          title={o.product?.sku || ''}>
                      <span className="font-mono font-semibold text-slate-700">{o.value}</span>
                      <span className="text-slate-400 max-w-[220px] truncate">{o.product?.name}</span>
                    </span>
                  ))}
                </div>
              </section>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
